// src/services/signalStore.js
import { jwtDecode } from 'jwt-decode';

const DB_NAME = 'signal-keys';
const DB_VERSION = 3;
const CACHE_DB_VERSION = 3;
const STORE_NAME = 'keys';
const CACHE_DB = 'decrypted-messages-cache';
const CACHE_STORE = 'texts';
const FILE_KEYS_STORE = 'file-keys';
const GROUP_MESSAGE_KEYS_STORE = 'group-message-keys';

function openCacheDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(
      CACHE_DB,
      CACHE_DB_VERSION
    );

    req.onupgradeneeded = () => {
      const db = req.result;

      

      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        db.createObjectStore(CACHE_STORE);
      }

      if (!db.objectStoreNames.contains(FILE_KEYS_STORE)) {
        db.createObjectStore(FILE_KEYS_STORE);
      }

      if (!db.objectStoreNames.contains(GROUP_MESSAGE_KEYS_STORE)) {
        db.createObjectStore(GROUP_MESSAGE_KEYS_STORE);
      }
    };

    req.onsuccess = () => {
      const db = req.result;

      

      resolve(db);
    };

    req.onerror = () => {
      console.error('❌ CACHE OPEN ERROR', req.error);
      reject(req.error);
    };
  });
}
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

function arrayBuffersEqual(a, b) {
  if (!a || !b) return false;
  const bufA = a instanceof ArrayBuffer ? new Uint8Array(a) : new Uint8Array(a.buffer || a);
  const bufB = b instanceof ArrayBuffer ? new Uint8Array(b) : new Uint8Array(b.buffer || b);
  if (bufA.length !== bufB.length) return false;
  for (let i = 0; i < bufA.length; i++) {
    if (bufA[i] !== bufB[i]) return false;
  }
  return true;
}

function getCurrentUserId() {
  const token = localStorage.getItem('token');
  if (!token) return 'anonymous';
  try {
    const decoded = jwtDecode(token);
    return decoded.id;
  } catch {
    return 'anonymous';
  }
}

// Stockage générique clé/valeur pour libsignal
export const signalStore = {
  async get(key) {
    const scopedKey = `${getCurrentUserId()}:${key}`;
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(scopedKey);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  },
  async put(key, value) {
    const scopedKey = `${getCurrentUserId()}:${key}`;
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(value, scopedKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  },
  async remove(key) {
    const scopedKey = `${getCurrentUserId()}:${key}`;
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(scopedKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  },

  // ---- Interface SignalProtocolStore attendue par SessionBuilder / SessionCipher ----

  async getIdentityKeyPair() {
    return this.get('identityKey'); // { pubKey, privKey }
  },

  async getLocalRegistrationId() {
    return this.get('registrationId');
  },

  async isTrustedIdentity(identifier, identityKey /*, direction */) {
    const trusted = await this.get(`identity_${identifier}`);
    if (!trusted) {
      // Trust On First Use (TOFU) : aucune identité connue pour ce contact → on fait confiance
      return true;
    }
    return arrayBuffersEqual(trusted, identityKey);
  },

  async saveIdentity(identifier, identityKey) {
    const existing = await this.get(`identity_${identifier}`);
    await this.put(`identity_${identifier}`, identityKey);
    // true si l'identité a changé par rapport à ce qui était connu (utile pour alerter l'utilisateur)
    return existing ? !arrayBuffersEqual(existing, identityKey) : false;
  },

  async loadPreKey(keyId) {
    const res = await this.get(`preKey_${keyId}`);
    return res || undefined;
  },

  async storePreKey(keyId, keyPair) {
    await this.put(`preKey_${keyId}`, keyPair);
  },

  async removePreKey(keyId) {
    await this.remove(`preKey_${keyId}`);
  },

  async loadSignedPreKey(keyId) {
    const signed = await this.get('signedPreKey');
    if (!signed || signed.keyId !== keyId) return undefined;
    return signed.keyPair;
  },

  async storeSignedPreKey(keyId, keyPair) {
    await this.put('signedPreKey', { keyId, keyPair });
  },

  async removeSignedPreKey(keyId) {
    const signed = await this.get('signedPreKey');
    if (signed && signed.keyId === keyId) {
      await this.remove('signedPreKey');
    }
  },

  async loadSession(identifier) {
  const result = await this.get(`session_${identifier}`);
  
  return result;
},

async storeSession(identifier, record) {
  
  await this.put(`session_${identifier}`, record);
},


  async removeSession(identifier) {
    await this.remove(`session_${identifier}`);
  },

  async removeAllSessions(identifier) {
    // Optionnel selon la lib : supprime toutes les sessions liées à un identifiant de base (tous devices confondus)
    await this.remove(`session_${identifier}`);
  },

};

export async function getCachedGroupMessageKey(messageId) {
  const db = await openCacheDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(
      GROUP_MESSAGE_KEYS_STORE,
      'readonly'
    );

    const store = tx.objectStore(
      GROUP_MESSAGE_KEYS_STORE
    );

    const req = store.get(messageId);

    req.onsuccess = () => {
      if (!req.result) {
        resolve(null);
        return;
      }

      resolve(
        req.result instanceof Uint8Array
          ? req.result
          : new Uint8Array(req.result)
      );
    };

    req.onerror = () => reject(req.error);
  });
}

export async function setCachedGroupMessageKey(
  messageId,
  aesKey
) {
  if (!messageId) {
    throw new Error(
      'setCachedGroupMessageKey: messageId manquant'
    );
  }

  if (!aesKey) {
    throw new Error(
      'setCachedGroupMessageKey: clé AES manquante'
    );
  }

  const key =
    aesKey instanceof Uint8Array
      ? aesKey
      : new Uint8Array(aesKey);

  if (key.byteLength !== 32) {
    throw new Error(
      `setCachedGroupMessageKey: clé invalide (${key.byteLength} octets)`
    );
  }

  const db = await openCacheDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(
      GROUP_MESSAGE_KEYS_STORE,
      'readwrite'
    );

    const store = tx.objectStore(
      GROUP_MESSAGE_KEYS_STORE
    );

    const req = store.put(key, messageId);

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getCachedDecryption(messageId) {
  const db = await openCacheDB();
  return new Promise((resolve) => {
    const tx = db.transaction(CACHE_STORE, 'readonly');
    const req = tx.objectStore(CACHE_STORE).get(messageId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(undefined);
  });
}

export async function setCachedDecryption(messageId, plaintext) {
  const db = await openCacheDB();
  return new Promise((resolve) => {
    const tx = db.transaction(CACHE_STORE, 'readwrite');
    tx.objectStore(CACHE_STORE).put(plaintext, messageId);
    tx.oncomplete = () => resolve();
  });
}

export async function getCachedFileKey(fileId) {
  const db = await openCacheDB();

  return new Promise((resolve) => {
    const tx = db.transaction(FILE_KEYS_STORE, 'readonly');
    const req = tx.objectStore(FILE_KEYS_STORE).get(fileId);

    req.onsuccess = () => {
      resolve(req.result);
    };

    req.onerror = () => {
      console.error(
        '❌ Impossible de récupérer la clé AES du fichier:',
        fileId,
        req.error
      );
      resolve(undefined);
    };
  });
}

export async function setCachedFileKey(fileId, aesKey) {
  const db = await openCacheDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(FILE_KEYS_STORE, 'readwrite');

    tx.objectStore(FILE_KEYS_STORE).put(aesKey, fileId);

    tx.oncomplete = () => {
      resolve();
    };

    tx.onerror = () => {
      reject(tx.error);
    };
  });
}

export const cacheKeyOf = (msg) => (msg.editVersion ? `${msg._id}#${msg.editVersion}` : msg._id);

export async function purgeMessageCaches(ids) {
  if (!ids?.length) return;
  const db = await openCacheDB();

  // clés concernées : `${id}` (texte), `${id}#${version}` (texte modifié), `${id}:${type}` (clé de fichier)
  const belongs = (key) => {
    const k = String(key);
    return ids.some((id) => k === id || k.startsWith(`${id}#`) || k.startsWith(`${id}:`));
  };

  for (const storeName of [CACHE_STORE, GROUP_MESSAGE_KEYS_STORE, FILE_KEYS_STORE]) {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        for (const key of req.result) if (belongs(key)) store.delete(key);
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}

//if (typeof window !== 'undefined') {
//  window.__signalStore = signalStore;
//}

export async function purgeSessionsAndPreKeys() {
  const prefix = `${getCurrentUserId()}:`;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAllKeys();
    req.onsuccess = () => {
      for (const key of req.result) {
        const k = String(key);
        if (!k.startsWith(prefix)) continue;
        const name = k.slice(prefix.length);
        if (
          name.startsWith('session_') ||
          name.startsWith('preKey_') ||
          name.startsWith('identity_') ||   // identités de confiance des correspondants (TOFU)
          name === 'nextPreKeyId'
        ) {
          store.delete(key);
        }
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}